import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SearchBar } from '../SearchBar';
import styles from './DropdownMenu.module.scss';

export interface DropdownProps {
    children: React.ReactNode;
    isOpen: boolean;
    toggleRef: React.RefObject<HTMLButtonElement | null>;
    onClose: () => void;
    className?: string;
    selected?: string;
    /** When set, shows SearchBar at the top of the dropdown. Reuses SearchBar component. */
    searchPlaceholder?: string;
    /** Controlled search value (optional). */
    searchValue?: string;
    /** Called when search input changes. Use to filter dropdown items. */
    onSearchChange?: (value: string) => void;
    /** Debounce delay for search in ms (default 150). */
    searchDebounceDelay?: number;
}

export const Dropdown: React.FC<DropdownProps> = ({
    children,
    isOpen,
    onClose,
    toggleRef,
    className,
    selected,
    searchPlaceholder,
    searchValue,
    onSearchChange,
    searchDebounceDelay = 150,
}) => {
    const showSearch = Boolean(searchPlaceholder);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement>(null);

    const calculatePosition = useCallback((): void => {
        if (!toggleRef.current || !menuRef.current) return;

        const buttonRect = toggleRef.current.getBoundingClientRect();
        const menuRect = menuRef.current;
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Skip calculation if menu hasn't been rendered yet
        if (menuRect.offsetWidth === 0 || menuRect.offsetHeight === 0) {
            setTimeout(() => calculatePosition(), 10);
            return;
        }

        // Position menu below the button by default
        let top = buttonRect.bottom + scrollY + 4;
        let left = buttonRect.left + scrollX;

        // Viewport boundary checks
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Horizontal boundary check - align to right if menu would overflow
        if (left + menuRect.offsetWidth > viewportWidth) {
            left = buttonRect.right + scrollX - menuRect.offsetWidth;
        }

        // Ensure menu doesn't go off the left edge
        if (left < scrollX) {
            left = scrollX + 8;
        }

        // Vertical boundary check - show above button if there's not enough space below
        if (top + menuRect.offsetHeight > scrollY + viewportHeight) {
            const spaceAbove = buttonRect.top - scrollY;
            const spaceBelow = viewportHeight - buttonRect.bottom;

            if (spaceAbove > spaceBelow && spaceAbove >= menuRect.offsetHeight) {
                // Show above button
                top = buttonRect.top + scrollY - menuRect.offsetHeight - 4;
            } else {
                // Keep below but adjust to fit in viewport
                top = buttonRect.top + scrollY - menuRect.offsetHeight - 8;
            }
        }

        setMenuPosition({ top, left });
    }, []);

    useEffect(() => {
        if (isOpen) {
            // Use double requestAnimationFrame to ensure DOM has fully updated
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    calculatePosition();
                });
            });
        }
    }, [isOpen, calculatePosition]);

    // Update position on window resize or scroll
    useEffect(() => {
        if (!isOpen) return;

        window.addEventListener('resize', calculatePosition);
        window.addEventListener('scroll', calculatePosition, true);

        return () => {
            window.removeEventListener('resize', calculatePosition);
            window.removeEventListener('scroll', calculatePosition, true);
        };
    }, [isOpen, calculatePosition]);

    // Close the dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                menuRef.current &&
                toggleRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                !toggleRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (selected && isOpen) {
            const selectedItem = menuRef.current?.querySelector(`[data-value="${selected}"]`);
            if (selectedItem) {
                selectedItem.scrollIntoView({ block: 'center' });
            }
        }
    }, [selected, isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div
            ref={menuRef}
            className={`${styles.menu} ${className}`}
            style={{
                position: 'absolute',
                top: menuPosition.top || -9999,
                left: menuPosition.left || -9999,
            }}
        >
            {showSearch && (
                <div className={styles.searchWrapper} onClick={(e) => e.stopPropagation()}>
                    <SearchBar
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onSearch={onSearchChange ?? (() => {})}
                        onChange={onSearchChange}
                        debounceDelay={searchDebounceDelay}
                        testId='dropdown-search'
                    />
                </div>
            )}
            {children}
        </div>,
        document.body
    );
};
